import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Lock, User, LogIn, RefreshCw, WifiOff } from 'lucide-react';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSetup, setIsSetup] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNetworkError(false);

    // Retry logic for network issues
    const maxRetries = 3;
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }

      try {
        if (isSetup) {
          await authApi.setup(username, password);
          toast.success('Admin account created! Please login.');
          setIsSetup(false);
          setLoading(false);
          return;
        } else {
          const response = await authApi.login(username, password);
          localStorage.setItem('admin_token', response.data.access_token);
          localStorage.setItem('admin_username', response.data.username);
          toast.success('Login successful!');
          navigate('/admin');
          return;
        }
      } catch (error) {
        lastError = error;
        
        // If it's a network error, continue retrying
        if (!error.response) {
          console.error(`Login attempt ${attempt + 1} failed - network error`);
          continue;
        }
        
        // If it's an auth error, don't retry
        const message = error.response?.data?.detail || 'An error occurred';
        toast.error(message);
        setLoading(false);
        return;
      }
    }

    // All retries failed
    setLoading(false);
    if (!lastError?.response) {
      setNetworkError(true);
      toast.error('No se pudo conectar con el servidor. Por favor, inténtalo de nuevo.');
    }
  };

  if (networkError) {
    return (
      <div 
        className="min-h-dvh flex items-center justify-center p-4"
        style={{ backgroundColor: '#FDFCF8' }}
      >
        <Card className="w-full max-w-md" style={{ backgroundColor: '#F5F2EA' }}>
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <WifiOff className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-stone-800 mb-3">Conexión perdida</h2>
              <p className="text-stone-500 mb-6 text-sm">
                No se pudo conectar con el servidor. Verifica tu conexión a internet.
              </p>
              <Button
                onClick={() => setNetworkError(false)}
                className="w-full"
                style={{ backgroundColor: '#7C895A', color: 'white' }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-dvh flex items-center justify-center p-4"
      style={{ backgroundColor: '#FDFCF8' }}
      data-testid="admin-login-screen"
    >
      
      <Card 
        className="w-full max-w-md"
        style={{ backgroundColor: '#F5F2EA', border: '1px solid rgba(44, 44, 42, 0.08)' }}
      >
        <CardHeader className="text-center">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-white"
            style={{ backgroundColor: '#7C895A' }}
          >
            <Lock size={28} />
          </div>
          <CardTitle 
            className="text-2xl"
            style={{ fontFamily: "'Playfair Display', serif", color: '#2C2C2A' }}
          >
            {isSetup ? 'Create Admin' : 'Admin Login'}
          </CardTitle>
          <CardDescription style={{ color: '#5C5C59' }}>
            {isSetup 
              ? 'Set up your admin account'
              : 'Access the Bistró Pont10 admin panel'
            }
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" style={{ color: '#2C2C2A' }}>
                Username
              </Label>
              <div className="relative">
                <User 
                  className="absolute left-3 top-1/2 -translate-y-1/2" 
                  size={18} 
                  style={{ color: '#8C8C89' }}
                />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  placeholder="Enter username"
                  required
                  data-testid="admin-username-input"
                  style={{ backgroundColor: '#FDFCF8' }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: '#2C2C2A' }}>
                Password
              </Label>
              <div className="relative">
                <Lock 
                  className="absolute left-3 top-1/2 -translate-y-1/2" 
                  size={18} 
                  style={{ color: '#8C8C89' }}
                />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  placeholder="Enter password"
                  required
                  data-testid="admin-password-input"
                  style={{ backgroundColor: '#FDFCF8' }}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              style={{ backgroundColor: '#7C895A', color: 'white' }}
              data-testid="admin-login-btn"
            >
              {loading ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                <>
                  <LogIn size={18} className="mr-2" />
                  {isSetup ? 'Create Account' : 'Login'}
                </>
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setIsSetup(!isSetup)}
              className="text-sm underline"
              style={{ color: '#7C895A' }}
              data-testid="toggle-setup-btn"
            >
              {isSetup ? 'Back to login' : 'First time? Create admin account'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
